import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

/**
 * Store для управления состоянием страницы данных (таблица продуктов)
 * Сохраняет фильтры, пагинацию, сортировку и настройки отображения
 */
export const useDataStore = defineStore('data', () => {
  // ========== Фильтры таблицы ==========
  const filters = ref({})
  const globalFilterValue = ref('')
  
  // ========== Пагинация ==========
  const first = ref(0) // Индекс первой записи на текущей странице
  const rows = ref(10) // Количество строк на странице
  
  // ========== Сортировка ==========
  const sortField = ref(null)
  const sortOrder = ref(null)
  
  // ========== Выбранные продукты ==========
  const selectedProducts = ref([])
  
  // ========== Настройки колонок ==========
  const visibleColumns = ref([
    'part_number',
    'brand',
    'category',
    'type',
    'size',
    'color'
  ])
  
  // ========== Диалоги ==========
  const showAddDialog = ref(false)
  const showEditDialog = ref(false)
  const showExportDialog = ref(false)
  const showMappingSettings = ref(false)
  const showBrandProducerDialog = ref(false)
  const showReportsModal = ref(false)
  const showRestoreDialog = ref(false)
  const showConditionExceptionsDialog = ref(false)
  const showSiteStatusMappingsDialog = ref(false)

  // ========== Computed ==========
  const hasActiveFilters = computed(() => {
    return Object.keys(filters.value).length > 0 || globalFilterValue.value
  })

  // ========== Методы ==========
  
  /**
   * Сброс всех фильтров
   */
  function resetFilters() {
    filters.value = {}
    globalFilterValue.value = ''
  }
  
  /**
   * Сброс пагинации на первую страницу
   */
  function resetPagination() {
    first.value = 0
  }
  
  /**
   * Сброс сортировки
   */
  function resetSorting() {
    sortField.value = null
    sortOrder.value = null
  }
  
  /**
   * Очистка выбора
   */
  function clearSelection() {
    selectedProducts.value = []
  }
  
  /**
   * Переключение видимости колонки
   */
  function toggleColumn(columnName) {
    const index = visibleColumns.value.indexOf(columnName)
    if (index > -1) {
      visibleColumns.value.splice(index, 1)
    } else {
      visibleColumns.value.push(columnName)
    }
  }
  
  /**
   * Сброс всех настроек колонок к дефолтным
   */
  function resetColumns() {
    visibleColumns.value = [
      'part_number',
      'brand',
      'category',
      'type',
      'size',
      'color'
    ]
  }
  
  /**
   * Полный сброс состояния
   */
  function resetState() {
    resetFilters()
    resetPagination()
    resetSorting()
    clearSelection()
    showAddDialog.value = false
    showEditDialog.value = false
    showExportDialog.value = false
    showMappingSettings.value = false
    showBrandProducerDialog.value = false
    showReportsModal.value = false
    showRestoreDialog.value = false
    showConditionExceptionsDialog.value = false
    showSiteStatusMappingsDialog.value = false
  }
  
  return {
    // State
    filters,
    globalFilterValue,
    first,
    rows,
    sortField,
    sortOrder,
    selectedProducts,
    visibleColumns,
    showAddDialog,
    showEditDialog,
    showExportDialog,
    showMappingSettings,
    showBrandProducerDialog,
    showReportsModal,
    showRestoreDialog,
    showConditionExceptionsDialog,
    showSiteStatusMappingsDialog,

    // Computed
    hasActiveFilters,
    
    // Methods
    resetFilters,
    resetPagination,
    resetSorting,
    clearSelection,
    toggleColumn,
    resetColumns,
    resetState
  }
}, {
  // Персистентность: сохраняем фильтры и настройки отображения
  persist: {
    key: 'ai-database-data',
    storage: sessionStorage,
    paths: [
      'filters',
      'globalFilterValue',
      'first',
      'rows',
      'sortField',
      'sortOrder',
      'visibleColumns'
    ]
  }
})
